import { z } from 'zod';
import type { RequestFileMime } from '../requests/request-files.js';

export const CARGO_PROBLEM_PUBLIC_CODE_REGEX = /^PR-\d{5}$/;
export const CARGO_PROBLEM_DESCRIPTION_MAX = 4_000;
export const CARGO_PROBLEM_COMMENT_MAX = 4_000;
export const CARGO_PROBLEM_COMMENT_PREVIEW_MAX = 200;
/**
 * Live (non-deleted) attachments one problem may hold across all of its
 * messages. Nothing soft-deletes yet, so today's enforcement counts every row;
 * the deletion-aware count arrives with the moderation path (#2865).
 */
export const CARGO_PROBLEM_FILES_MAX = 100;
/** Attachments one message may carry; a message with zero attachments stays legal. */
export const CARGO_PROBLEM_MESSAGE_ATTACHMENTS_MAX = 10;
/** Participant-visible reason recorded when a published attachment is deleted. */
export const CARGO_PROBLEM_DELETE_REASON_MAX = 200;
export const CARGO_PROBLEM_FILE_MAX_BYTES = 25 * 1024 * 1024;
export const CARGO_PROBLEM_VIDEO_MAX_BYTES = 200 * 1024 * 1024;
/** Page bounds for the cursor-paginated workspace stream, per the lead-activity feed. */
export const CARGO_PROBLEM_STREAM_PAGE_DEFAULT = 50;
export const CARGO_PROBLEM_STREAM_PAGE_MAX = 100;
export const CARGO_PROBLEM_LIST_MAX = 300;
export const CARGO_PROBLEM_SEARCH_MIN = 2;
export const CARGO_PROBLEM_CARGO_CANDIDATES_MAX = 20;

export const CargoProblemCategorySchema = z.enum([
  'SUPPLIER',
  'CHINA_WAREHOUSE',
  'CHINA_CARRIER',
  'RUSSIA_WAREHOUSE',
  'RUSSIA_CARRIER',
]);
export type CargoProblemCategory = z.infer<typeof CargoProblemCategorySchema>;

export const CargoProblemQueueSchema = z.enum(['CHINA_LOGISTICS', 'RUSSIA_LOGISTICS']);
export type CargoProblemQueue = z.infer<typeof CargoProblemQueueSchema>;

/**
 * @aiInvariant append-only, literal-for-literal with the Prisma
 *   `CargoProblemEventKind` enum (`scripts/check-enum-sync.sh`). Growing it
 *   widens the `/activity` wire enum — an accepted expand per the
 *   `NotificationEntityType` / `LeadActivityKind` precedent: an old parser
 *   rejects only a row of a new kind, never an existing one.
 * @aiInvariant reaction escalation is deliberately absent. `CargoProblemEvent`
 *   requires an FK-anchored `actorId`, and the escalation writer is a
 *   scheduler with no principal; escalation durability lives in
 *   `CargoProblem.reactionEscalatedAt` plus the notification/audit rows.
 */
export const CargoProblemEventKindSchema = z.enum([
  'SUBMITTED',
  'REJECTED',
  'CLAIMED',
  'REVIEW_SUBMITTED',
  'EVALUATED',
  'EDITED',
  'RESUBMITTED',
  'COMMENT_ADDED',
  'FILE_UPLOADED',
  'ASSIGNEE_CHANGED',
  'ATTACHMENT_DELETED',
]);
export type CargoProblemEventKind = z.infer<typeof CargoProblemEventKindSchema>;

/**
 * @aiPurpose what an attachment was filed under: the author's initial brief,
 *   a chat message, or a workflow note. Literal-for-literal with the Prisma
 *   `CargoProblemAttachmentContext` enum.
 * @aiInvariant legacy rows carry NO context (the column and the wire field are
 *   nullable); nothing back-fills them, so `null` means "filed before the
 *   workspace shipped" rather than an inferred bucket.
 */
export const CargoProblemAttachmentContextSchema = z.enum(['BRIEF', 'MESSAGE', 'WORKFLOW']);
export type CargoProblemAttachmentContext = z.infer<typeof CargoProblemAttachmentContextSchema>;

/**
 * @aiPurpose the subset of the accepted attachment MIME allowlist that reads as
 *   a document rather than as evidence media. A message whose set contains one
 *   of these requires a nonblank body — a bare invoice tells the recipient
 *   nothing about what to do with it.
 */
export const CARGO_PROBLEM_DOCUMENT_MIME = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
] as const satisfies readonly RequestFileMime[];
export type CargoProblemDocumentMime = (typeof CARGO_PROBLEM_DOCUMENT_MIME)[number];

/** True when the content type requires an accompanying message body. */
export function isCargoProblemDocumentMime(contentType: string): boolean {
  return (CARGO_PROBLEM_DOCUMENT_MIME as readonly string[]).includes(contentType);
}

export const CargoProblemTransitionActionSchema = z.enum([
  'submit',
  'reject',
  'claim',
  'submitReview',
  'evaluate',
]);
export type CargoProblemTransitionAction = z.infer<typeof CargoProblemTransitionActionSchema>;
